import { useState } from 'react';
import Layout from '../components/Layout';
import { CheckCircle, AlertCircle, Loader2, ArrowRight, HelpCircle, Plus, X, ChevronDown } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATES = [
  { value: '', label: 'Select State' },
  { value: 'HI', label: 'Hawaii (HI)' },
  { value: 'NV', label: 'Nevada (NV)' },
  { value: 'IL', label: 'Illinois (IL)' },
  { value: 'CA', label: 'California (CA)' },
  { value: 'TX', label: 'Texas (TX)' },
  { value: 'FL', label: 'Florida (FL)' },
  { value: 'NY', label: 'New York (NY)' },
  { value: 'AZ', label: 'Arizona (AZ)' },
  { value: 'CO', label: 'Colorado (CO)' },
  { value: 'WA', label: 'Washington (WA)' },
  { value: 'OR', label: 'Oregon (OR)' },
  { value: 'GA', label: 'Georgia (GA)' },
  { value: 'NC', label: 'North Carolina (NC)' },
  { value: 'NJ', label: 'New Jersey (NJ)' },
  { value: 'PA', label: 'Pennsylvania (PA)' },
  { value: 'MA', label: 'Massachusetts (MA)' },
  { value: 'OTHER', label: 'Other' },
];

// Component for relationship accounts entry (On/Off-Premise)
function RelationshipAccountsEntry({ index, entry, onChange, onRemove, canRemove }) {
  return (
    <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-navy">State #{index + 1}</span>
        {canRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="text-slate-400 hover:text-red-500 transition-colors"
            aria-label="Remove this state entry"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
      
      {/* State Dropdown */}
      <div>
        <label className="block text-sm font-medium text-navy mb-2">
          State
        </label>
        <div className="relative">
          <select
            value={entry.state}
            onChange={(e) => onChange({ ...entry, state: e.target.value })}
            className="input-field appearance-none pr-10"
            data-testid={`relationship-state-${index}`}
          >
            {STATES.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
        </div>
      </div>

      {/* On-Premise Accounts */}
      <div>
        <label className="block text-sm font-medium text-navy mb-2">
          On-Premise Accounts
        </label>
        <textarea
          value={entry.onPremise}
          onChange={(e) => onChange({ ...entry, onPremise: e.target.value })}
          className="input-field resize-none"
          rows={2}
          placeholder="List your best On-Premise accounts (restaurants, bars, hotels...)"
          data-testid={`relationship-on-premise-${index}`}
        />
      </div>

      {/* Off-Premise Accounts */}
      <div>
        <label className="block text-sm font-medium text-navy mb-2">
          Off-Premise Accounts
        </label>
        <textarea
          value={entry.offPremise}
          onChange={(e) => onChange({ ...entry, offPremise: e.target.value })}
          className="input-field resize-none"
          rows={2}
          placeholder="List your best Off-Premise accounts (retailers, liquor stores...)"
          data-testid={`relationship-off-premise-${index}`}
        />
      </div>
    </div>
  );
}

// Component for distributor relationships entry
function DistributorRelationshipsEntry({ index, entry, onChange, onRemove, canRemove }) {
  return (
    <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-navy">State #{index + 1}</span>
        {canRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="text-slate-400 hover:text-red-500 transition-colors"
            aria-label="Remove this distributor entry"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
      
      {/* State Dropdown */}
      <div>
        <label className="block text-sm font-medium text-navy mb-2">
          State
        </label>
        <div className="relative">
          <select
            value={entry.state}
            onChange={(e) => onChange({ ...entry, state: e.target.value })}
            className="input-field appearance-none pr-10"
            data-testid={`distributor-state-${index}`}
          >
            {STATES.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
        </div>
      </div>

      {/* Distributor */}
      <div>
        <label className="block text-sm font-medium text-navy mb-2">
          Distributor
        </label>
        <input
          type="text"
          value={entry.distributor}
          onChange={(e) => onChange({ ...entry, distributor: e.target.value })}
          className="input-field"
          placeholder="e.g., Southern Glazer's, RNDC, Breakthru..."
          data-testid={`distributor-name-${index}`}
        />
      </div>

      {/* Key Distributor Relationships */}
      <div>
        <label className="block text-sm font-medium text-navy mb-2">
          Key Distributor Relationships
        </label>
        <textarea
          value={entry.keyRelationships}
          onChange={(e) => onChange({ ...entry, keyRelationships: e.target.value })}
          className="input-field resize-none"
          rows={2}
          placeholder="List key contacts/reps you have strong relationships with..."
          data-testid={`distributor-relationships-${index}`}
        />
      </div>
    </div>
  );
}

export default function RequestAccess() {
  // Apply SEO meta tags
  useSEO('requestAccess');

  const [role, setRole] = useState('Buyer');
  const [status, setStatus] = useState({ state: 'idle', msg: '' });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    state: '',
    channel_or_category: '',
    notes: '',
    membership_tier: 'tier_2',
    addon_ai_support: false,
    addon_intro_support: false
  });

  // Supplier-specific: Rock solid relationship states
  const [rockSolidStates, setRockSolidStates] = useState(['']);

  // Supplier-specific: Best relationship accounts by state (On/Off-Premise)
  const [relationshipAccounts, setRelationshipAccounts] = useState([
    { state: '', onPremise: '', offPremise: '' }
  ]);

  // Supplier-specific: Distributor relationships by state
  const [distributorRelationships, setDistributorRelationships] = useState([
    { state: '', distributor: '', keyRelationships: '' }
  ]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleRockSolidStateChange = (index, value) => {
    const updated = [...rockSolidStates];
    updated[index] = value;
    setRockSolidStates(updated);
  };

  const addRockSolidState = () => {
    setRockSolidStates([...rockSolidStates, '']);
  };

  const removeRockSolidState = (index) => {
    setRockSolidStates(rockSolidStates.filter((_, i) => i !== index));
  };

  const handleRelationshipAccountChange = (index, entry) => {
    const updated = [...relationshipAccounts];
    updated[index] = entry;
    setRelationshipAccounts(updated);
  };

  const addRelationshipAccount = () => {
    setRelationshipAccounts([...relationshipAccounts, { state: '', onPremise: '', offPremise: '' }]);
  };

  const removeRelationshipAccount = (index) => {
    setRelationshipAccounts(relationshipAccounts.filter((_, i) => i !== index));
  };

  const handleDistributorRelationshipChange = (index, entry) => {
    const updated = [...distributorRelationships];
    updated[index] = entry;
    setDistributorRelationships(updated);
  };

  const addDistributorRelationship = () => {
    setDistributorRelationships([...distributorRelationships, { state: '', distributor: '', keyRelationships: '' }]);
  };

  const removeDistributorRelationship = (index) => {
    setDistributorRelationships(distributorRelationships.filter((_, i) => i !== index));
  };

  async function onSubmit(e) {
    e.preventDefault();
    setStatus({ state: 'loading', msg: '' });

    // Build payload with supplier-specific fields
    const payload = {
      role,
      ...formData,
      // Only include supplier fields if role is Supplier
      ...(role === 'Supplier' && {
        membership_tier: formData.membership_tier,
        addon_ai_support: formData.addon_ai_support,
        addon_intro_support: formData.addon_intro_support,
        rock_solid_states: rockSolidStates.filter(s => s !== ''),
        relationship_accounts: relationshipAccounts.filter(r => r.state !== ''),
        distributor_relationships: distributorRelationships.filter(d => d.state !== '')
      })
    };

    try {
      const res = await fetch(`${API}/request-access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data?.detail || 'Request failed');
      }
      
      setStatus({ state: 'success', msg: 'Request received. We\'ll follow up shortly.' });
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        state: '',
        channel_or_category: '',
        notes: '',
        membership_tier: 'tier_2',
        addon_ai_support: false,
        addon_intro_support: false
      });
      setRockSolidStates(['']);
      setRelationshipAccounts([{ state: '', onPremise: '', offPremise: '' }]);
      setDistributorRelationships([{ state: '', distributor: '', keyRelationships: '' }]);
      setRole('Buyer');
    } catch (err) {
      setStatus({ state: 'error', msg: err.message });
    }
  }

  return (
    <Layout>
      <section 
        className="min-h-screen bg-slate-50 py-12" 
        data-testid="request-access-section"
        aria-labelledby="request-access-heading"
      >
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Left Side - Info */}
            <header className="lg:sticky lg:top-24">
              <span className="inline-block text-copper font-semibold text-sm tracking-wider uppercase mb-4">
                Get Started
              </span>
              <h1 
                id="request-access-heading"
                className="font-heading text-4xl md:text-5xl font-bold text-navy leading-tight mb-6"
              >
                Request Access to BevRelay
              </h1>
              <p className="text-slate-600 text-lg leading-relaxed mb-8">
                Buyer access is free and approved to keep the network real. 
                Supplier access is curated by category to maintain quality.
              </p>

              {/* Image */}
              <div className="rounded-3xl overflow-hidden shadow-xl hidden lg:block">
                <img 
                  src="https://images.unsplash.com/photo-1758599543152-a73184816eba?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHw0fHxidXNpbmVzcyUyMGhhbmRzaGFrZSUyMG1lZXRpbmclMjByZXN0YXVyYW50JTIwb3duZXJ8ZW58MHx8fHwxNzcyMjUyNjg1fDA&ixlib=rb-4.1.0&q=85"
                  alt="Business professionals shaking hands, representing BevRelay partnership connections"
                  className="w-full h-64 object-cover"
                  loading="lazy"
                />
              </div>

              {/* Info Cards */}
              <div className="mt-8 space-y-4">
                <article className="rounded-2xl bg-white p-6 border border-slate-200">
                  <h2 className="font-semibold text-navy mb-2">For Buyers</h2>
                  <p className="text-slate-600 text-sm">
                    Free access to browse curated suppliers and request meetings or samples. 
                    No cost, no commitments.
                  </p>
                </article>
                <article className="rounded-2xl bg-white p-6 border border-slate-200">
                  <h2 className="font-semibold text-navy mb-2">For Suppliers — From $499/year</h2>
                  <p className="text-slate-600 text-sm">
                    Three membership tiers to fit your stage. Share your account and distributor relationships to strengthen the coalition network. Brand Builder access also available as a one-time purchase.
                  </p>
                </article>
              </div>

              {/* FAQ for this page */}
              <div className="mt-8 rounded-2xl bg-navy/5 p-6 border border-navy/10">
                <div className="flex items-start gap-3">
                  <HelpCircle className="w-5 h-5 text-navy flex-shrink-0 mt-0.5" aria-hidden="true" />
                  <div>
                    <h3 className="font-semibold text-navy mb-1">How long does approval take?</h3>
                    <p className="text-sm text-slate-600">
                      Most buyer applications are reviewed within 24-48 hours. 
                      Supplier applications may take longer as we carefully curate each category.
                    </p>
                  </div>
                </div>
              </div>
            </header>

            {/* Right Side - Form */}
            <div>
              <form 
                onSubmit={onSubmit} 
                className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm"
                data-testid="request-access-form"
                aria-labelledby="form-heading"
              >
                <h2 id="form-heading" className="sr-only">Request Access Form</h2>
                
                {/* Role Selection */}
                <div className="mb-6">
                  <label htmlFor="role-select" className="block text-sm font-medium text-navy mb-2">
                    I am a
                  </label>
                  <select
                    id="role-select"
                    name="role"
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                    className="input-field"
                    data-testid="role-select"
                  >
                    <option value="Buyer">Buyer (Free Access)</option>
                    <option value="Supplier">Supplier (Annual Membership)</option>
                  </select>
                </div>

                {/* Form Fields */}
                <div className="grid gap-5 md:grid-cols-2">
                  <div>
                    <label htmlFor="name-input" className="block text-sm font-medium text-navy mb-2">
                      Name <span className="text-red-500">*</span>
                    </label>
                    <input 
                      id="name-input"
                      name="name" 
                      value={formData.name}
                      onChange={handleInputChange}
                      required 
                      className="input-field"
                      data-testid="name-input"
                      placeholder="Your full name"
                      autoComplete="name"
                    />
                  </div>
                  <div>
                    <label htmlFor="email-input" className="block text-sm font-medium text-navy mb-2">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input 
                      id="email-input"
                      name="email" 
                      type="email" 
                      value={formData.email}
                      onChange={handleInputChange}
                      required 
                      className="input-field"
                      data-testid="email-input"
                      placeholder="you@company.com"
                      autoComplete="email"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone-input" className="block text-sm font-medium text-navy mb-2">
                      Phone
                    </label>
                    <input 
                      id="phone-input"
                      name="phone" 
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="input-field"
                      data-testid="phone-input"
                      placeholder="(555) 123-4567"
                      autoComplete="tel"
                    />
                  </div>
                  <div>
                    <label htmlFor="company-input" className="block text-sm font-medium text-navy mb-2">
                      {role === 'Buyer' ? 'Company / Account' : 'Company / Brand'} <span className="text-red-500">*</span>
                    </label>
                    <input 
                      id="company-input"
                      name="company" 
                      value={formData.company}
                      onChange={handleInputChange}
                      required 
                      className="input-field"
                      data-testid="company-input"
                      placeholder={role === 'Buyer' ? 'Your business name' : 'Your brand name'}
                      autoComplete="organization"
                    />
                  </div>
                  <div>
                    <label htmlFor="state-input" className="block text-sm font-medium text-navy mb-2">
                      Primary State
                    </label>
                    <div className="relative">
                      <select
                        id="state-input"
                        name="state"
                        value={formData.state}
                        onChange={handleInputChange}
                        className="input-field appearance-none pr-10"
                        data-testid="state-input"
                      >
                        {STATES.map((s) => (
                          <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="channel-input" className="block text-sm font-medium text-navy mb-2">
                      {role === 'Buyer' ? 'Channel' : 'Primary Category'}
                    </label>
                    <input
                      id="channel-input"
                      name="channel_or_category"
                      value={formData.channel_or_category}
                      onChange={handleInputChange}
                      className="input-field"
                      data-testid="channel-input"
                      placeholder={role === 'Buyer' ? 'Retail / On-Premise / Chain...' : 'Tequila / Champagne / Wine / Beer...'}
                    />
                  </div>
                </div>

                {/* Membership Tier Selection - Only for Suppliers */}
                {role === 'Supplier' && (
                  <div className="mt-5">
                    <label htmlFor="membership-tier" className="block text-sm font-medium text-navy mb-2">
                      Select Membership Tier <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <select
                        id="membership-tier"
                        name="membership_tier"
                        value={formData.membership_tier || 'tier_2'}
                        onChange={handleInputChange}
                        className="input-field appearance-none pr-10"
                        data-testid="membership-tier-select"
                      >
                        <option value="tier_1">Tier 1 - $499/year</option>
                        <option value="tier_2">Tier 2 - $888/year (Most Popular)</option>
                        <option value="tier_3">Tier 3 - $1,500/year</option>
                        <option value="brand_builder_only">Brand Builder Only - $199 (One-Time)</option>
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                    </div>
                  </div>
                )}

                {/* Monthly Add-Ons - Checkboxes */}
                {role === 'Supplier' && (
                  <div className="mt-6 p-5 rounded-xl bg-slate-50 border border-slate-200">
                    <label className="block text-sm font-medium text-navy mb-3">
                      Optional Monthly Add-Ons
                    </label>
                    <div className="space-y-3">
                      <label className="flex items-start gap-3 cursor-pointer group">
                        <input
                          type="checkbox"
                          name="addon_ai_support"
                          checked={formData.addon_ai_support || false}
                          onChange={(e) => setFormData(prev => ({ ...prev, addon_ai_support: e.target.checked }))}
                          className="mt-1 w-4 h-4 rounded border-slate-300 text-copper focus:ring-copper"
                          data-testid="addon-ai-checkbox"
                        />
                        <div>
                          <span className="text-sm font-medium text-navy group-hover:text-copper transition-colors">
                            Additional AI Support — $99/mo
                          </span>
                          <p className="text-xs text-slate-500 mt-0.5">
                            Extended AI workforce services: social media, content creation, operational support
                          </p>
                        </div>
                      </label>
                      <label className="flex items-start gap-3 cursor-pointer group">
                        <input
                          type="checkbox"
                          name="addon_intro_support"
                          checked={formData.addon_intro_support || false}
                          onChange={(e) => setFormData(prev => ({ ...prev, addon_intro_support: e.target.checked }))}
                          className="mt-1 w-4 h-4 rounded border-slate-300 text-copper focus:ring-copper"
                          data-testid="addon-intro-checkbox"
                        />
                        <div>
                          <span className="text-sm font-medium text-navy group-hover:text-copper transition-colors">
                            Dedicated Intro Support — $199/mo
                          </span>
                          <p className="text-xs text-slate-500 mt-0.5">
                            Personalized introduction facilitation and meeting coordination
                          </p>
                        </div>
                      </label>
                    </div>
                  </div>
                )}

                {/* ============ SUPPLIER-SPECIFIC FIELDS ============ */}
                {role === 'Supplier' && (
                  <div className="mt-8 pt-8 border-t border-slate-200">
                    <h3 className="font-heading font-semibold text-lg text-navy mb-4">
                      Your Relationship Network
                    </h3>
                    <p className="text-sm text-slate-600 mb-6">
                      Help us understand your strongest market presence. This information helps us curate the coalition effectively.
                    </p>

                    {/* Rock Solid Relationship States */}
                    <div className="mb-8">
                      <label className="block text-sm font-medium text-navy mb-3">
                        States Where You Have Rock Solid Relationships
                      </label>
                      <div className="space-y-3">
                        {rockSolidStates.map((state, index) => (
                          <div key={index} className="flex items-center gap-2">
                            <div className="relative flex-1">
                              <select
                                value={state}
                                onChange={(e) => handleRockSolidStateChange(index, e.target.value)}
                                className="input-field appearance-none pr-10"
                                data-testid={`rock-solid-state-${index}`}
                              >
                                {STATES.map((s) => (
                                  <option key={s.value} value={s.value}>{s.label}</option>
                                ))}
                              </select>
                              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                            </div>
                            {rockSolidStates.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeRockSolidState(index)}
                                className="p-2 text-slate-400 hover:text-red-500 transition-colors"
                                aria-label="Remove state"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                      <button
                        type="button"
                        onClick={addRockSolidState}
                        className="mt-3 inline-flex items-center gap-2 text-sm text-copper font-medium hover:text-copper-dark transition-colors"
                        data-testid="add-rock-solid-state-btn"
                      >
                        <Plus className="w-4 h-4" />
                        Add Another State
                      </button>
                    </div>

                    {/* Best Relationship Accounts by State (On/Off-Premise) */}
                    <div className="mb-8">
                      <label className="block text-sm font-medium text-navy mb-3">
                        Best Account Relationships by State
                      </label>
                      <p className="text-xs text-slate-500 mb-4">
                        List your strongest On-Premise and Off-Premise accounts in each state where you have significant presence.
                      </p>
                      
                      <div className="space-y-4">
                        {relationshipAccounts.map((entry, index) => (
                          <RelationshipAccountsEntry
                            key={index}
                            index={index}
                            entry={entry}
                            onChange={(updated) => handleRelationshipAccountChange(index, updated)}
                            onRemove={() => removeRelationshipAccount(index)}
                            canRemove={relationshipAccounts.length > 1}
                          />
                        ))}
                      </div>

                      <button
                        type="button"
                        onClick={addRelationshipAccount}
                        className="mt-4 inline-flex items-center gap-2 text-sm text-copper font-medium hover:text-copper-dark transition-colors"
                        data-testid="add-relationship-account-btn"
                      >
                        <Plus className="w-4 h-4" />
                        Add Another State's Accounts
                      </button>
                    </div>

                    {/* Distributor Relationships by State */}
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-navy mb-3">
                        Distributor Relationships by State
                      </label>
                      <p className="text-xs text-slate-500 mb-4">
                        List your distributor and key relationships in each state. This helps coordinate coalition presentations.
                      </p>
                      
                      <div className="space-y-4">
                        {distributorRelationships.map((entry, index) => (
                          <DistributorRelationshipsEntry
                            key={index}
                            index={index}
                            entry={entry}
                            onChange={(updated) => handleDistributorRelationshipChange(index, updated)}
                            onRemove={() => removeDistributorRelationship(index)}
                            canRemove={distributorRelationships.length > 1}
                          />
                        ))}
                      </div>

                      <button
                        type="button"
                        onClick={addDistributorRelationship}
                        className="mt-4 inline-flex items-center gap-2 text-sm text-copper font-medium hover:text-copper-dark transition-colors"
                        data-testid="add-distributor-relationship-btn"
                      >
                        <Plus className="w-4 h-4" />
                        Add Another State's Distributor
                      </button>
                    </div>
                  </div>
                )}

                {/* Notes */}
                <div className="mt-5">
                  <label htmlFor="notes-textarea" className="block text-sm font-medium text-navy mb-2">
                    Additional Notes
                  </label>
                  <textarea
                    id="notes-textarea"
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    rows={4}
                    className="input-field resize-none"
                    data-testid="notes-textarea"
                    placeholder={role === 'Buyer'
                      ? 'What categories are you sourcing? Any specific timing or needs?'
                      : 'Tell us about your markets, positioning, and what you\'re looking for in the coalition.'}
                  />
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={status.state === 'loading'}
                  className="mt-6 w-full rounded-xl bg-navy px-5 py-4 text-white font-semibold transition-all duration-200 hover:bg-navy-800 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="submit-btn"
                >
                  {status.state === 'loading' ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      Submit Request
                      <ArrowRight className="w-4 h-4" aria-hidden="true" />
                    </>
                  )}
                </button>

                {/* Status Messages */}
                {status.state !== 'idle' && status.state !== 'loading' && (
                  <div
                    className={`mt-4 rounded-xl px-4 py-3 flex items-start gap-3 ${
                      status.state === 'success' 
                        ? 'bg-green-50 text-green-800 border border-green-200'
                        : 'bg-red-50 text-red-800 border border-red-200'
                    }`}
                    data-testid="form-status"
                    role="alert"
                  >
                    {status.state === 'success' ? (
                      <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" aria-hidden="true" />
                    ) : (
                      <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" aria-hidden="true" />
                    )}
                    <span className="text-sm">{status.msg}</span>
                  </div>
                )}

                {/* Trust Signals */}
                <div className="mt-6 pt-6 border-t border-slate-100">
                  <p className="text-xs text-slate-500 text-center">
                    By submitting, you agree to be contacted about BevRelay opportunities. 
                    We respect your privacy and never share your information.
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
